"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Settings, LogOut, ChevronDown, User, Camera, Wallet, CircleUser } from "lucide-react";
import { studentMenuItems } from "@/components/dashboard/StudentDashboard/StudentSidebar";
import { teacherMenuItems } from "@/components/dashboard/TeacherDashboard/TeacherSidebar";
import { adminMenuItems } from "@/components/dashboard/AdminDashboard/AdminSidebar";
import { useLogout } from "@/hooks/useLogout";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";

// Common shape across roles. `labelKey` is optional because not every role's
// sidebar has been internationalized yet (admin still renders `title`).
type MobileNavItem = {
  title: string;
  url: string;
  icon: typeof Settings;
  labelKey?: string;
};

const menuItemsByRole: Record<string, MobileNavItem[]> = {
  STUDENT: studentMenuItems,
  TEACHER: teacherMenuItems,
  ADMIN: adminMenuItems,
};

// Label + icon for the bottom collapsible account group. Mirrors each
// role's desktop sidebar: admin calls it "Profile" (CircleUser) because
// admin already has a separate "Settings" → platform settings item in the
// flat menu above; student/teacher call the same group "Settings".
const settingsGroupByRole: Record<
  string,
  { title: string; labelKey: string; icon: typeof User }
> = {
  STUDENT: { title: "Settings", labelKey: "settings", icon: Settings },
  TEACHER: { title: "Settings", labelKey: "settings", icon: Settings },
  ADMIN: { title: "Profile", labelKey: "profile", icon: CircleUser },
};

const settingsSubItemsByRole: Record<
  string,
  { title: string; labelKey: string; url: string; icon: typeof User }[]
> = {
  STUDENT: [
    { title: "Profile", labelKey: "profile", url: "/dashboard/settings", icon: User },
    { title: "Profile Picture", labelKey: "profilePicture", url: "/dashboard/settings/profile-picture", icon: Camera },
  ],
  TEACHER: [
    { title: "Profile", labelKey: "profile", url: "/dashboard/settings", icon: User },
    { title: "Payout Details", labelKey: "payoutDetails", url: "/dashboard/settings/payout", icon: Wallet },
    { title: "Profile Picture", labelKey: "profilePicture", url: "/dashboard/settings/profile-picture", icon: Camera },
  ],
  ADMIN: [
    { title: "Profile", labelKey: "profile", url: "/dashboard/settings", icon: User },
    { title: "Profile Picture", labelKey: "profilePicture", url: "/dashboard/settings/profile-picture", icon: Camera },
  ],
};

export function DashboardMobileNav({
  role,
  onNavigate,
}: {
  role: string;
  onNavigate: () => void;
}) {
  const pathname = usePathname();
  const tNav = useTranslations("Nav");
  // Resolve a Nav label by key, falling back to the English title if the key
  // hasn't been added yet (keeps the menu working as translations roll out).
  const label = (item: { labelKey?: string; title: string }) =>
    item.labelKey && tNav.has(item.labelKey) ? tNav(item.labelKey) : item.title;
  const { handleLogout, isLoggingOut } = useLogout();
  const isSettingsPage = role === "ADMIN"
    ? pathname === "/dashboard/settings" || pathname === "/dashboard/settings/profile-picture"
    : pathname.startsWith("/dashboard/settings");
  const [settingsOpen, setSettingsOpen] = useState(true);

  const menuItems = menuItemsByRole[role] ?? [];
  const settingsSubItems = settingsSubItemsByRole[role] ?? [];
  const settingsGroup = settingsGroupByRole[role] ?? { title: "Settings", icon: Settings };
  const SettingsGroupIcon = settingsGroup.icon;

  const linkStyles =
    "text-sm font-light text-white hover:text-gray-300";
  const activeLinkStyles =
    "text-sm font-light text-[#F6C65A] active-underline";

  const isActive = (item: { title: string; url: string }) =>
    item.title === "Home"
      ? pathname === item.url
      : pathname === item.url || pathname.startsWith(item.url + "/");

  return (
    <nav className="flex flex-col gap-4">
      {menuItems.map((item) => (
        <Link
          key={item.title}
          href={item.url}
          data-testid={`mobileNavLink${item.title.replace(/\s+/g, "")}`}
          onClick={onNavigate}
          className={`flex items-center gap-3 ${isActive(item) ? activeLinkStyles : linkStyles}`}
        >
          <item.icon className="h-5 w-5" />
          {label(item)}
        </Link>
      ))}

      <div className="my-2 border-t border-white/20" />

      <button
        onClick={() => setSettingsOpen((o) => !o)}
        data-testid="mobileNavAccountToggle"
        className={`flex cursor-pointer items-center gap-3 ${
          isSettingsPage ? activeLinkStyles : linkStyles
        }`}
      >
        <SettingsGroupIcon className="h-5 w-5" />
        <span className="flex-1 text-start">{label(settingsGroup)}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${settingsOpen ? "rotate-180" : ""}`}
        />
      </button>

      {settingsOpen &&
        settingsSubItems.map((sub) => (
          <Link
            key={sub.title}
            href={sub.url}
            onClick={onNavigate}
            className={`flex items-center gap-3 ps-8 ${
              pathname === sub.url ? activeLinkStyles : linkStyles
            }`}
          >
            <sub.icon className="h-4 w-4" />
            {label(sub)}
          </Link>
        ))}

      <button
        onClick={() => {
          onNavigate();
          handleLogout();
        }}
        disabled={isLoggingOut}
        className={`flex cursor-pointer items-center gap-3 text-start text-sm font-light ${
          isLoggingOut
            ? "cursor-not-allowed text-white/50"
            : "text-white hover:text-gray-300"
        }`}
      >
        <LogOut className="h-5 w-5" />
        {isLoggingOut ? tNav("loggingOut") : tNav("logout")}
      </button>

      <div className="mt-2 border-t border-white/20 pt-4">
        <LanguageSwitcher tone="dark" />
      </div>
    </nav>
  );
}
